/**
 * Which items a family's dashboard is allowed to raise.
 *
 * The parent dashboard is an inbox, and a school decides what belongs in it.
 * Low grades ship switched off: a struggling subject is a conversation a school
 * wants to have itself, not something a parent discovers on a list.
 */
import { useEffect, useMemo, useState } from 'react';
import { Label } from '~/components/ui/label';
import { Switch } from '~/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { SettingsSaveBar } from './SettingsPanel';
import { useUpdateParentAlerts } from './useUpdateParentAlerts';

export interface ParentAlertsSetting {
  enabled: boolean;
  invoiceEmails: boolean;
  invoiceEmailsSince?: string | null;
  invoiceOverdue: boolean;
  invoiceDue: boolean;
  enrollmentAction: boolean;
  missingWork: boolean;
  lowGrade: boolean;
  showGradeColors: boolean;
  lowGradeFloorLetter?: string | null;
}

interface GradeScaleEntry {
  letter: string;
  minimumPercentage: number;
}

interface Props {
  schoolId?: string;
  setting?: ParentAlertsSetting | null;
  gradeScaleEntries?: ReadonlyArray<GradeScaleEntry>;
  onSaved?: () => void;
}

type AlertKey = 'invoiceOverdue' | 'invoiceDue' | 'enrollmentAction' | 'missingWork' | 'lowGrade';

/**
 * Named the way a parent would describe what landed on their dashboard, not by
 * the state machine behind it.
 */
const ALERTS: ReadonlyArray<{ key: AlertKey; label: string; help: string }> = [
  {
    key: 'invoiceOverdue',
    label: 'Overdue tuition and failed payments',
    help: 'Tuition past its due date, and cards that were declined.',
  },
  {
    key: 'invoiceDue',
    label: 'Tuition coming due',
    help: 'Unpaid tuition that still has time on it.',
  },
  {
    key: 'enrollmentAction',
    label: 'Unfinished enrollment',
    help: 'An invitation or application the family has not submitted.',
  },
  {
    key: 'missingWork',
    label: 'Missing work',
    help: 'Assignments a teacher marked as not submitted.',
  },
  {
    key: 'lowGrade',
    label: 'Low class grades',
    help: 'Current standings that fall below the grade you choose.',
  },
];

const DEFAULTS: ParentAlertsSetting = {
  enabled: false,
  invoiceEmails: false,
  invoiceEmailsSince: null,
  invoiceOverdue: true,
  invoiceDue: true,
  enrollmentAction: true,
  missingWork: true,
  lowGrade: false,
  showGradeColors: true,
  lowGradeFloorLetter: null,
};

export default function ParentAlertsEditor({ schoolId, setting, gradeScaleEntries, onSaved }: Props) {
  const [draft, setDraft] = useState<ParentAlertsSetting>(DEFAULTS);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updateParentAlerts, { loading }] = useUpdateParentAlerts();

  useEffect(() => {
    setDraft(setting ? { ...DEFAULTS, ...setting } : DEFAULTS);
    setDirty(false);
    setError(null);
  }, [setting]);

  const letters = useMemo(
    () => [...(gradeScaleEntries ?? [])].sort((a, b) => b.minimumPercentage - a.minimumPercentage),
    [gradeScaleEntries]
  );

  const floor = letters.find((entry) => entry.letter === draft.lowGradeFloorLetter);

  const set = (patch: Partial<ParentAlertsSetting>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    if (!schoolId) return;
    setError(null);

    try {
      const result = await updateParentAlerts({
        variables: {
          input: {
            schoolId,
            enabled: draft.enabled,
            invoiceEmails: draft.invoiceEmails,
            invoiceOverdue: draft.invoiceOverdue,
            invoiceDue: draft.invoiceDue,
            enrollmentAction: draft.enrollmentAction,
            missingWork: draft.missingWork,
            lowGrade: draft.lowGrade,
            showGradeColors: draft.showGradeColors,
            lowGradeFloorLetter: draft.lowGradeFloorLetter || null,
          },
        },
      });

      const errors = result.data?.updateParentAlerts?.userErrors;
      if (errors?.length) {
        setError(errors.map((item) => item.message).join(', '));
        return;
      }

      setDirty(false);
      onSaved?.();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save these alerts');
    }
  };

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4 rounded-lg border p-4">
        <div className="min-w-0">
          <Label htmlFor="parent-experience" className="text-sm font-medium">
            Let families sign in
          </Label>
          <p className="mt-0.5 max-w-[62ch] text-sm text-muted-foreground">
            Gives a parent a dashboard, their children&rsquo;s pages, their household record,
            and their invoices. With this off, a parent who signs in is told the school has
            not opened it yet. Enrollment and application links sent by email keep working
            either way.
          </p>
        </div>
        <Switch
          id="parent-experience"
          checked={draft.enabled}
          onCheckedChange={(checked) => set({ enabled: checked })}
        />
      </div>

      <div className={`mb-6 flex items-start justify-between gap-4 rounded-lg border p-4 ${draft.enabled ? '' : 'opacity-60'}`}>
        <div className="min-w-0">
          <Label htmlFor="invoice-emails" className="text-sm font-medium">
            Email families when an invoice is posted
          </Label>
          <p className="mt-0.5 max-w-[62ch] text-sm text-muted-foreground">
            When staff post an invoice for a family to pay, every guardian with an email address
            hears about it, with a link to view and pay &mdash; or to set up their account first.
            Invoices that charge automatically are never emailed. Nothing posted before this is
            switched on is announced.
          </p>
          {draft.invoiceEmails && draft.invoiceEmailsSince && (
            <p className="mt-1 text-sm text-muted-foreground">
              On since {new Date(draft.invoiceEmailsSince).toLocaleDateString()}.
            </p>
          )}
          {!draft.enabled && (
            <p className="mt-1 text-sm text-muted-foreground">
              Let families sign in first; the email needs somewhere to send them.
            </p>
          )}
        </div>
        <Switch
          id="invoice-emails"
          disabled={!draft.enabled}
          checked={draft.enabled && draft.invoiceEmails}
          onCheckedChange={(checked) => set({ invoiceEmails: checked })}
        />
      </div>

      <h2 className="mb-2 font-medium">Needs you</h2>
      <p className="mb-4 max-w-[62ch] text-sm text-muted-foreground">
        Anything switched off still lives on the family&rsquo;s own pages. This only decides
        what their dashboard raises under &ldquo;Needs you&rdquo;.
      </p>

      <div className={`divide-y rounded-lg border ${draft.enabled ? '' : 'opacity-60'}`}>
          {ALERTS.map((alert) => (
            <div key={alert.key} className="flex items-start justify-between gap-4 p-4">
              <div className="min-w-0">
                <Label htmlFor={`alert-${alert.key}`} className="text-sm font-medium">
                  {alert.label}
                </Label>
                <p className="mt-0.5 text-sm text-muted-foreground">{alert.help}</p>

                {alert.key === 'lowGrade' && draft.lowGrade && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Label htmlFor="low-grade-floor" className="text-sm text-muted-foreground">
                      Raise a grade below
                    </Label>
                    <Select
                      value={draft.lowGradeFloorLetter ?? ''}
                      onValueChange={(value) => set({ lowGradeFloorLetter: value })}
                    >
                      <SelectTrigger id="low-grade-floor" className="w-28">
                        <SelectValue placeholder="Grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {letters.map((entry) => (
                          <SelectItem key={entry.letter} value={entry.letter}>
                            {entry.letter}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">
                      {floor
                        ? `under ${floor.minimumPercentage}%`
                        : 'from your grade scale'}
                    </span>
                  </div>
                )}
              </div>
              <Switch
                id={`alert-${alert.key}`}
                disabled={!draft.enabled}
                checked={draft[alert.key]}
                onCheckedChange={(checked) => set({ [alert.key]: checked } as Partial<ParentAlertsSetting>)}
              />
            </div>
          ))}
      </div>

      <h2 className="mb-2 mt-8 font-medium">Grades</h2>
      <div className={`flex items-start justify-between gap-4 rounded-lg border p-4 ${draft.enabled ? '' : 'opacity-60'}`}>
        <div className="min-w-0">
          <Label htmlFor="show-grade-colors" className="text-sm font-medium">
            Show grade colors
          </Label>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Shows a grade in the color your grade scale gives it. With this off a family still
            sees the grade and the letter, just not in color. Teachers and administrators always
            see the colors.
          </p>
        </div>
        <Switch
          id="show-grade-colors"
          disabled={!draft.enabled}
          checked={draft.showGradeColors}
          onCheckedChange={(checked) => set({ showGradeColors: checked })}
        />
      </div>

      <SettingsSaveBar dirty={dirty} saving={loading} error={error} onSave={save} />
    </>
  );
}
